// Layer 3 (Transformation) — the typed PM schedule (a20-maintenance S5a).
// Pure: no React, no engine mutations, no clock reads of its own.
//
// Two things happen here. The first is generation: every planned stoppage in
// the 30-day basis becomes a completed PM instance, so the compliance read and
// the downtime event log cannot disagree — the log is the one place a stopping
// PM is recorded, and this file reads it rather than carrying a second copy.
// The second is derivation: status, the four KPI reads and the row orders are
// all computed from `dueDate` and `completedAt` against a cursor this file is
// handed, never seeded.
//
// Status is compared on CALENDAR DATES, never times: a task due at 08:00 with
// the cursor at 11:30 is due today, not overdue, and a task due on the 15th is
// outside a week that ends at midnight on the 15th. The cursor is floored to
// its own date before every comparison — the raw cursor's time of day would
// otherwise move tasks between the due-today and this-week buckets.
//
// The cursor is a parameter, never `DEFAULT_CURSOR`: the tab passes the
// session cursor and the probe passes the schema's default, so the same
// projection answers for any "now" the product carries.
//
// durable: PM status and compliance projection over a plan plus a stoppage log (ADR-0012).

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import { addMinutes, diffMinutes, formatDayMonth } from '@/versions/v1/shared/clock.derive';
import {
  BASIS_DAYS,
  BASIS_END,
  BASIS_START,
  DOWNTIME_ITEMS,
} from './downtime.seed';
import {
  downtimeEvents,
} from './DowntimeEvents.transform';
import { formatHours } from './Downtime.transform';
import {
  SHUTDOWN_CREW_LABEL,
  TYPED_TASKS,
  type PmTask,
} from './pm.seed';

export type PmStatus = 'complete' | 'overdue' | 'due-today' | 'scheduled';

/** The one wording for each status, so the pill, the probe and the doc agree. */
export const STATUS_LABEL: Record<PmStatus, string> = {
  complete: 'Complete',
  overdue: 'Overdue',
  'due-today': 'Due today',
  scheduled: 'Scheduled',
};

export type PmTaskRow = {
  id: string;
  asset: string;
  task: string;
  /** `10 Jun` — formatted once here so the row and the sort key cannot drift. */
  due: string;
  dueDate: SeriesTime;
  crew: string;
  hours: number;
  status: PmStatus;
  statusLabel: string;
  overdue: boolean;
};

export type CompletedPmRow = PmTaskRow & {
  /** `17 May` — the day the work finished. */
  done: string;
  /** True when the work finished after its own due date. */
  late: boolean;
};

/** The cursor's own calendar day. Every comparison below is made on this
 *  floor: the time of day a task is due at is a scheduling detail, while the
 *  day it is due on is what the reader is being told. */
function cursorDay(cursor: SeriesTime): SeriesTime {
  return `${cursor.slice(0, 10)}T00:00`;
}

function dayOf(at: SeriesTime): string {
  return at.slice(0, 10);
}

/** A PM's status, from its own two dates and the cursor. Completion wins over
 *  lateness — a task finished after its due date is complete, and its lateness
 *  is what the compliance read counts, not what the status says. */
export function pmStatus(task: PmTask, cursor: SeriesTime): PmStatus {
  if (task.completedAt !== undefined) return 'complete';
  const due = dayOf(task.dueDate);
  const today = dayOf(cursor);
  if (due < today) return 'overdue';
  if (due === today) return 'due-today';
  return 'scheduled';
}

/** A stopping PM is due on the day its own window opens, so a generated
 *  instance is on time by construction; every point of variance in the
 *  compliance read below comes from the non-stopping typed rows. */
export function completedFromEvents(): PmTask[] {
  return downtimeEvents()
    .filter((event) => event.kind === 'planned' && diffMinutes(event.start, BASIS_START) >= 0)
    .map((event) => ({
      id: `pm-${event.id}`,
      itemId: event.itemId,
      asset: DOWNTIME_ITEMS.find((row) => row.id === event.itemId)?.label ?? event.itemId,
      task: event.subCause,
      dueDate: event.start,
      estHours: event.hours,
      crew: SHUTDOWN_CREW_LABEL,
      completedAt: event.end,
      sourceEventId: event.id,
    }));
}

/** The whole schedule: what the log says happened, then what the plan says is
 *  coming. No `PM_TASKS` constant exists — a frozen copy of this would be a
 *  second schedule to keep in step. */
export function pmTasks(): PmTask[] {
  return [...completedFromEvents(), ...TYPED_TASKS];
}

export type PmKpis = {
  dueToday: { count: number; hours: number; value: string; sub: string };
  overdue: {
    count: number;
    hours: number;
    worst: { asset: string; daysOverdue: number } | null;
    value: string;
    sub: string;
  };
  thisWeek: {
    count: number;
    hours: number;
    label: string;
    technicianCount: number;
    value: string;
    sub: string;
  };
  compliance: { onTime: number; due: number; pct: number; value: string; sub: string };
};

/** The two steps of the compliance decode. The band's meter draws its ticks
 *  at these values: they decode the tone, they are not a target. */
export const COMPLIANCE_OK_PCT = 90;
export const COMPLIANCE_WARN_PCT = 75;

/** `ok` at or above 90, `warn` at or above 75, `bad` below — the three-step
 *  decode the compliance cell reads. */
export function complianceTone(pct: number): VizTone {
  if (pct >= COMPLIANCE_OK_PCT) return 'ok';
  if (pct >= COMPLIANCE_WARN_PCT) return 'warn';
  return 'bad';
}

/** The four cards. `dueToday`, `overdue` and `thisWeek` read the open rows;
 *  `compliance` reads the closed ones over the 30-day basis, so the four cards
 *  are one schedule read four ways.
 *
 *  No completion date on an open row means hours is what the work is expected
 *  to take, which is what the two hour-carrying cards print. The week ends
 *  seven days on from the cursor's own day and the bound is exclusive on the
 *  date, so a task due on the eighth day falls out of the week. */
export function pmKpis(tasks: readonly PmTask[], cursor: SeriesTime): PmKpis {
  const today = cursorDay(cursor);
  const todayDay = dayOf(today);
  const weekEnd = addMinutes(today, 7 * 24 * 60);
  const weekEndDay = dayOf(weekEnd);

  const dueTodayRows: PmTask[] = [];
  const overdueRows: PmTask[] = [];
  const thisWeekRows: PmTask[] = [];

  for (const task of tasks) {
    const status = pmStatus(task, cursor);
    if (status === 'due-today') dueTodayRows.push(task);
    else if (status === 'overdue') overdueRows.push(task);
    const due = dayOf(task.dueDate);
    if (status !== 'complete' && due >= todayDay && due < weekEndDay) thisWeekRows.push(task);
  }

  const dueTodayHours = dueTodayRows.reduce((sum, task) => sum + task.estHours, 0);
  const overdueHours = overdueRows.reduce((sum, task) => sum + task.estHours, 0);

  let worst: { asset: string; daysOverdue: number } | null = null;
  for (const task of overdueRows) {
    const daysOverdue = diffMinutes(today, `${dayOf(task.dueDate)}T00:00`) / (24 * 60);
    if (worst === null || daysOverdue > worst.daysOverdue) {
      worst = { asset: task.asset, daysOverdue };
    }
  }

  const weekLabel = `${formatDayMonth(today)} – ${formatDayMonth(addMinutes(today, 6 * 24 * 60))}`;
  const weekHours = thisWeekRows.reduce((sum, task) => sum + task.estHours, 0);
  const technicians = new Set(thisWeekRows.map((task) => task.crew)).size;

  const basisTasks = tasks.filter(
    (task) => task.dueDate >= BASIS_START && task.dueDate < BASIS_END,
  );
  const onTime = basisTasks.filter(
    (task) =>
      task.completedAt !== undefined && dayOf(task.completedAt) <= dayOf(task.dueDate),
  ).length;
  const due = basisTasks.length;
  const pct = due > 0 ? (onTime / due) * 100 : 0;

  return {
    dueToday: {
      count: dueTodayRows.length,
      hours: dueTodayHours,
      value: String(dueTodayRows.length),
      sub: formatHours(dueTodayHours),
    },
    overdue: {
      count: overdueRows.length,
      hours: overdueHours,
      worst,
      value: String(overdueRows.length),
      // With nothing overdue the card still measures: the sub reads the
      // hour count a fully-buffered reading would, never a sentence.
      sub: worst === null ? formatHours(0) : `${worst.asset} · ${worst.daysOverdue} days`,
    },
    thisWeek: {
      count: thisWeekRows.length,
      hours: weekHours,
      label: weekLabel,
      technicianCount: technicians,
      value: String(thisWeekRows.length),
      sub: `${formatHours(weekHours)} · ${technicians} technicians · ${weekLabel}`,
    },
    compliance: {
      onTime,
      due,
      pct,
      value: `${Math.round(pct)}%`,
      sub: `${onTime} of ${due} on time · ${BASIS_DAYS} days`,
    },
  };
}

/** The open rows, in the order they will be worked: earliest due date first,
 *  the id breaking a tie so two tasks due the same day never swap places
 *  between renders. */
export function pmTaskRows(tasks: readonly PmTask[], cursor: SeriesTime): PmTaskRow[] {
  return tasks
    .map((task) => ({ task, status: pmStatus(task, cursor) }))
    .filter(({ status }) => status !== 'complete')
    .sort(({ task: a }, { task: b }) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .map(({ task, status }) => {
      return {
        id: task.id,
        asset: task.asset,
        task: task.task,
        due: formatDayMonth(task.dueDate),
        dueDate: task.dueDate,
        crew: task.crew,
        hours: task.estHours,
        status,
        statusLabel: STATUS_LABEL[status],
        overdue: status === 'overdue',
      };
    });
}

/** The closed rows, most recently finished first — the id breaking a tie, in
 *  reverse, so two instances ending the same minute hold a stable order. */
export function completedPmRows(tasks: readonly PmTask[]): CompletedPmRow[] {
  return tasks
    .filter((task) => task.completedAt !== undefined)
    .sort((a, b) => {
      const aDone = a.completedAt ?? '';
      const bDone = b.completedAt ?? '';
      return (aDone < bDone ? 1 : aDone > bDone ? -1 : 0) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);
    })
    .map((task) => {
      const done = task.completedAt as SeriesTime;
      return {
        id: task.id,
        asset: task.asset,
        task: task.task,
        due: formatDayMonth(task.dueDate),
        dueDate: task.dueDate,
        crew: task.crew,
        hours: task.estHours,
        status: 'complete' as PmStatus,
        statusLabel: STATUS_LABEL.complete,
        overdue: false,
        done: formatDayMonth(done),
        late: dayOf(done) > dayOf(task.dueDate),
      };
    });
}
